import React, { memo, useContext, useState } from "react";
import { MeatballMenu } from "ui/common/dropdown-menu/meatball-menu";
import { routeConstants } from "app/routes/route-constants";
import { Link } from 'react-router-dom';
import { PromptConfirmationModal } from "ui/common/prompt-confirmation/prompt-confirmation-modal";
import { getHostingPath } from "app/infrastructure/hosting";
import { PrintLink } from "app/components/case-header-panel";
import { AddLossPotentialModal } from "app/area/case/components/loss-potential/add-loss-potential-evaluation-modal";
import { AddRiskEvaluationInitialValues } from "app/area/case/features/loss-potential-evaluation/types";
import { withGetUiDefinitionFromEntities } from "app/area/case/features/loss-potential-evaluation";
import { useSelectors } from "app/area/case-report/case-table-view/hooks/case-table-view-hooks";
import {
  ShareCase, SharePreset, UnlockCase, UnlockPreset, withSharingFromTextPath, withUnlockingFromTextPath
} from "app/area/case/features";
import { STATE } from "app/constants";
import { isValidUrl } from "app/infrastructure/validation/url";
import { DisplayLink, Link as ExternalLink } from 'ui';
import { urlToSurveyManager } from 'app/area/favourite/components/url-to-survey-manager';
import { ChangeCaseStatus } from "app/components/change-case-status/change-case-status";
import { AddNewConnectedCasesWireData } from "app/area/case/features/calls-to-actions/types";
import { UserType } from "app/types-shared";
import { AddNewConnectedCasesDropdown } from "app/components/add-new-cases-dropdown/add-new-connected-cases-dropdown";
import { useSharedState } from "app/infrastructure/redux/hooks";
import styles from "./case-table-view-meatball-menu.scss";
import { CaseTableViewContext } from "app/area/case-report/case-table-view/case-table-view-context";
import { getUrlOpenCase } from "app/components/helpers";

interface CaseTableViewMeatballMenuProps {
  caseId: number;
  singleSupplierUrl?: string;
}

function CaseTableViewMeatballMenuMemo(props: CaseTableViewMeatballMenuProps) {
  const { caseId, singleSupplierUrl } = props;
  const { translation, onDeleteCase, tableViewRef, onCopyRiskEvaluation, callsToActions: contextCallsToActions,
    onChangeCaseStatus, location, searchParams } = useContext(CaseTableViewContext);
  const [callsToActions, setCallsToActions] = useState(undefined);
  const { entities, access } = useSelectors();

  const sharedState = useSharedState();
  const allowForCurrentUser = sharedState?.userType?.v !== UserType.basic;
  const { caseURL, caseEditUrl, caseDuplicateUrl, caseBowtieUrl } = React.useMemo(() => ({
    caseURL: caseId ? routeConstants.caseViewFor(caseId) : "",
    caseEditUrl: caseId ? routeConstants.caseEditFor(caseId) : '',
    caseDuplicateUrl: caseId ? routeConstants.caseDuplicateFor(caseId) : '',
    caseBowtieUrl: caseId ? routeConstants.bowtieViewFor(caseId) : ''
  }), [caseId]);

  if (!caseId) {
    return null;
  }

  const onExpandMenu = async () => {
    const getChangeCaseStatusOptions = await contextCallsToActions?.(props.caseId);
    setCallsToActions(getChangeCaseStatusOptions);
  };

  const entitiesForSpecificCaseId = entities && entities.filter(entity => entity[0].CaseNo[0].value === caseId);
  const AddLossPotentialModalTableView = !!entitiesForSpecificCaseId?.length &&
    withGetUiDefinitionFromEntities(AddLossPotentialModal,
      Object.values(entitiesForSpecificCaseId[entitiesForSpecificCaseId.length - 1])[0]);

  const handleCopyEvaluation = (
    riskMatrixInstance: number, instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean
  ) => {
    onCopyRiskEvaluation?.(caseId, riskMatrixInstance, instanceCopyCmd, initialValues, copyToResidual);
  };

  const ShareCaseTableView: any = (withSharingFromTextPath(SharePreset.case, STATE.caseListing, caseId))(ShareCase);

  const UnlockCaseTableView: any = (withUnlockingFromTextPath(UnlockPreset.case, STATE.caseListing, caseId))(UnlockCase);

  return (
    <div className={styles.meatballMenuWrapper}>
      <MeatballMenu
        onExpandMenu={onExpandMenu}
        className={styles.meatballMenu}
        portalTarget={tableViewRef?.current}>
        {callsToActions?.editAccess && caseEditUrl &&
          <Link to={caseEditUrl} className="btn dropdown-item" target="_self">{translation.textEditCase}</Link>}

        {allowForCurrentUser && callsToActions?.lossPotentialSupported &&
          <AddLossPotentialModalTableView {...props}
            textValidFrom={translation.textEvaluationValidFrom}
            textEvaluationDescription={translation.textEvaluationDescription}
            textEvaluationHideInReport={translation.textEvaluationHideInReport}
            textAddEvaluationSubtitle={`${translation.textAddEvaluationSubtitle} ${' #'}${props.caseId}`}
            textAddItem={translation.textAddEvaluationTitle}
            textAdd={translation.textEvaluationAddItem}
            textAddResidualEvaluation={translation.textAddResidualEvaluation}
            textEvaluationCopyFromPreviousResidual={translation.textEvaluationCopyFromPreviousResidual}
            addButtonStyle='dropdownItem'
            allowCopyFromOtherInstance={callsToActions?.residualLossPotential}
            allowCopyEvaluationToResidual={callsToActions?.residualLossPotential}
            onCopyRiskEvaluation={handleCopyEvaluation} />}

        {allowForCurrentUser && !!callsToActions?.addNewConnectedCases?.length &&
          callsToActions?.addNewConnectedCases?.map((addNewConnectedCase: AddNewConnectedCasesWireData, index: number) => {
            return <AddNewConnectedCasesDropdown
              entityId={props.caseId}
              addNewConnectedCaseButtons={addNewConnectedCase.newCaseButtons}
              textLabelMenu={addNewConnectedCase.label}
              key={index}
              path={entitiesForSpecificCaseId[0]?.[0]?.id}
              {...props} />;
          })}

        {!!callsToActions?.changeStatus && callsToActions?.editAccess && allowForCurrentUser &&
          <ChangeCaseStatus
            caseId={props.caseId}
            changeCaseStatusOptions={callsToActions.changeStatus}
            changeCaseStatus={onChangeCaseStatus}
            textChangeStatus={translation.textChangeStatus}
            textWarningModalTitle={translation.textChangeStatusWarningModalTitle}
            textWarningModalDescription={translation.textChangeStatusWarningModalDescription}
            textWarningModalContinue={translation.textChangeStatusWarningModalContinue}
            textChangeStatusSuccess={translation.textChangeStatusSuccess}
            textChangeStatusWithCaseId={translation.textChangeStatusWithCaseId}
            textChangeStatusForReload={translation.textChangeStatusForReload}
            textChangeStatusValidationError={translation.textChangeStatusValidationError}
            textWarningModalEditCase={translation.textChangeStatusWarningModalEditCase}
            reloadOnChangeStatus={false}
            allowWarnings={false} />}
        
        {caseURL && 
          <DisplayLink value={caseId} 
            url={getUrlOpenCase(caseId, location?.pathname, searchParams)} 
            className="btn dropdown-item">{translation.textOpenCase}
          </DisplayLink>}

        {callsToActions?.bowtieSupported &&
          <Link to={caseBowtieUrl} className="btn dropdown-item" target="_self">{translation.textOpenBowtieTitle}</Link>}

        {callsToActions?.surveyManagerSupported && access?.surveyManager?.send &&
          isValidUrl(urlToSurveyManager(singleSupplierUrl, undefined, caseId)) &&
          <ExternalLink url={urlToSurveyManager(singleSupplierUrl, undefined, caseId)} linkText={translation.textOpenInSurveyManager}
            className={'dropdown-item'} />}

        {callsToActions?.editAccess &&
          <Link to={caseDuplicateUrl} className="btn dropdown-item" target="_self">{translation.textDuplicateCaseTitle}</Link>}

        {callsToActions?.editAccess &&
          <ShareCaseTableView {...props}>
            {
              ({ onOpen }) => <button type="button" className="dropdown-item" onClick={onOpen}>{translation.textShareCase}</button>
            }
          </ShareCaseTableView>}

        <PrintLink text={translation.textPrintCase} url={(getHostingPath() || '') + caseURL} />

        {callsToActions?.deleteAccess &&
          <PromptConfirmationModal
            title={translation.textDeleteCaseTitle}
            description={translation.textDeleteCaseDescription}
            continueText={translation.textDeleteCaseContinue}
            cancelText={translation.textDeleteCaseCancel}
            onContinue={() => onDeleteCase(caseId)}>
            <button type="button" className="dropdown-item">{translation.textDeleteCaseTitle}</button>
          </PromptConfirmationModal>
        }

        {callsToActions?.unlockAccess &&
          <UnlockCaseTableView {...props}>
            {
              ({ onOpen }) => <button type="button" className="dropdown-item" onClick={onOpen}>
                {translation.textUnlockCase}
              </button>
            }
          </UnlockCaseTableView>}
      </MeatballMenu>
    </div>
  );
}
export const CaseTableViewMeatballMenu = memo(CaseTableViewMeatballMenuMemo);
